import { useEffect } from "react";

/**
 * The Topic page's shortcuts (reduced set): `/` search, `o` overview, `m`
 * measure, `j` / `k` next or previous post, `Escape` close, `?` help.
 */
export const TOPIC_HOTKEYS = ["/", "o", "m", "j", "k", "Escape", "?"] as const;
export type TopicHotkey = (typeof TOPIC_HOTKEYS)[number];

export interface TopicHotkeyOptions {
  /** False keeps the handler registered but skipped. Defaults to true. */
  enabled?: boolean;
}

export type TopicHotkeyHandler = (event: KeyboardEvent) => void;

interface TopicHotkeyEntry {
  key: TopicHotkey;
  handler: TopicHotkeyHandler;
  enabled: boolean;
}

/** In registration order; the latest enabled entry for a key wins. */
const registry: Array<TopicHotkeyEntry> = [];

/**
 * Registers `handler` for `key` in the page's registry. Keys are ignored while
 * typing in an input, a textarea or an editable element (Escape included only
 * to blur), and with Meta, Ctrl or Alt held. When several handlers hold the
 * same key, the latest enabled one runs (a sheet opened over the page wins).
 * Returns the unregister function.
 */
export function registerTopicHotkey(
  key: TopicHotkey,
  handler: TopicHotkeyHandler,
  options: TopicHotkeyOptions = {},
): () => void {
  const entry: TopicHotkeyEntry = { key, handler, enabled: options.enabled ?? true };
  registry.push(entry);
  return () => {
    const index = registry.indexOf(entry);
    if (index >= 0) {
      registry.splice(index, 1);
    }
  };
}

function isTypingTarget(target: EventTarget | null): target is HTMLElement {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable || target.closest('[contenteditable]:not([contenteditable="false"])')) {
    return true;
  }
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

function isTopicHotkey(key: string): key is TopicHotkey {
  return (TOPIC_HOTKEYS as ReadonlyArray<string>).includes(key);
}

/** Pure dispatch of one keydown: what `useTopicHotkeys` listens with. Exported for tests. */
export function dispatchTopicHotkey(event: KeyboardEvent): void {
  if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) {
    return;
  }
  if (isTypingTarget(event.target)) {
    if (event.key === "Escape") {
      event.target.blur();
    }
    return;
  }
  if (!isTopicHotkey(event.key)) {
    return;
  }
  for (let index = registry.length - 1; index >= 0; index -= 1) {
    const entry = registry[index];
    if (entry.key === event.key && entry.enabled) {
      entry.handler(event);
      return;
    }
  }
}

/**
 * Installs the one keydown listener that dispatches to the registry while
 * the Topic page is mounted.
 */
export function useTopicHotkeys(): void {
  useEffect(() => {
    document.addEventListener("keydown", dispatchTopicHotkey);
    return () => {
      document.removeEventListener("keydown", dispatchTopicHotkey);
    };
  }, []);
}
